import BigNumber from 'bignumber.js';
import { format, type DateInput } from '@/lib/dayjs';
import type { Network } from '@/types';

export const AD_DAILY_LIMIT = 30;

export const DEVELOPER_TG_USER_ID = '5699547696';
export const CONSOLE_WALLET_ADDRESSES = {
  ROOT: '0xf6c2Cb41D35BB498ca06774ABb63a21AF97EE75b',
};

export const MOONINI_LOCK_ADMIN_ADDRESS = process.env.NEXT_PUBLIC_APP_ENV === 'production'
  ? '0xff2692e3656f025C2d8D90e75515B986fDc34084'
  : '0xf6c2Cb41D35BB498ca06774ABb63a21AF97EE75b';

export const MOONINI_LOCK_ADDRESS = process.env.NEXT_PUBLIC_APP_ENV === 'production'
  ? '0xa84c1fe00027a20bd196708470a52d4e5d62d081'
  : '0x2727cabc3562bec32c0588a400a82b4904f80aac';

export const NETWORKS: Record<string, Network> = {
  SOLANA: 'solana',
  BSC: 'bsc',
  ETHEREUM: 'ethereum',
  TON: 'ton',
  TRON: 'tron',
  BTC: 'bitcoin',
};

export const QUEUE_NAMES = {
  ACTION: 'action:queue',
};

export const LOG_TYPE = {
  // -
  GIFT_CREATED: 'gift',
  WITHDRAW: 'withdraw',
  WITHDRAW_FEE: 'withdraw:transaction_fee',
  // +
  GIFT_REFUNDED: 'gift_balance_refund',
  CHAT_FEE: 'chat_fee',
  DEPOSIT: 'deposit',
  WITHDRAW_FEE_REFUND: 'withdraw:transaction_fee_refund',
  WITHDRAW_REFUND: 'withdraw:refund',
  AD_REWARD: 'ad:reward',
  MISSION_REDEEMED: (mission_id?: string, redeemed_count?: string) => `mission_redeemed:${mission_id ?? ''}:${redeemed_count ?? ''}`,
  MISSION_REDEEMED_FOLLOW_X: (redeemed_count?: string) => `mission_redeemed:follow_x:${redeemed_count ?? ''}`,
  MISSION_REDEEMED_JOIN_CHANNEL: (redeemed_count?: string) => `mission_redeemed:join_channel:${redeemed_count ?? ''}`,
  MISSION_REDEEMED_JOIN_GROUP: (redeemed_count?: string) => `mission_redeemed:join_group:${redeemed_count ?? ''}`,
  MISSION_REDEEMED_INVITE: (redeemed_count?: string) => `mission_redeemed:invite:${redeemed_count ?? ''}`,
  GIFT_CLAIMED: (gift_id?: string) => `gift_claimed:${gift_id ?? ''}`,
  GIFT_BONUS: (gift_id?: string) => `gift_bonus:${gift_id ?? ''}`,
  MOONINI_AIRDROP: (gift_id?: string) => `moonini:airdrop:${gift_id ?? ''}`,
  SHARE_GIFT_SYSTEM_WITHDRAW: 'share_gift:system_withdraw',
  SHARE_GIFT_INVITED: (gift_id?: string) => `share_gift:invited:${gift_id ?? ''}`,
  SHARE_GIFT_CLAIMED: (gift_id?: string) => `share_gift:claimed:${gift_id ?? ''}`,
  /** AdsGram 廣告點擊來源記錄，用於轉換回傳 */
  ADSGRAM_CLICK: 'adsgram_click',
};

export const USDT_DECIMALS = 6;
export const BSC_USDT_DECIMALS = process.env.NEXT_PUBLIC_APP_ENV === 'production' ? 18 : 6;

export const MOONINI_UNALLOCATED_TYPE = 'moonini:unallocated';
export const MOONINI_SHARE_GIFT_CONSUMED_TYPE = 'moonini:share_gift_consumed';
export const MOONINI_DECIMALS = 18;
export const ONE_MOONINI = new BigNumber(1).multipliedBy(new BigNumber(10).pow(MOONINI_DECIMALS));
export const MIN_WITHDRAW_MOONINI = new BigNumber(10).multipliedBy(ONE_MOONINI); // 最低提领 10 moonini
export const MOONINI_NAME = 'Moonini';
export const MOONINI_SYMBOL = 'MOONINI';
export const MOONINI_ADDRESS = process.env.NEXT_PUBLIC_APP_ENV === 'production' ? '0xe4d5857271c7978F5fe6b29A73C97142D6d8Befe' : '0x6CB95dD3b0F70374aFAe919D1178Fb399fef569E';
export const MOONINI_TOKEN_ID = `${MOONINI_SYMBOL}:${MOONINI_DECIMALS}:${NETWORKS.BSC}:${MOONINI_ADDRESS}`;
export const MOONINI_TOTAL_SUPPLY = new BigNumber(10_000_000_000).multipliedBy(ONE_MOONINI);
export const MOONINI_TEAM_AND_SUPPORTER_AMOUNT = MOONINI_TOTAL_SUPPLY.multipliedBy(0.1);
export const MOONINI_IDO_AMOUNT = new BigNumber(1_000_000).multipliedBy(ONE_MOONINI);
export const MIN_EMISSION_VAULT_BALANCE = new BigNumber(1_000_000).multipliedBy(new BigNumber(10).pow(MOONINI_DECIMALS));
export const MOONINI_PUBLIC_OFFERING_AMOUNT = MOONINI_TOTAL_SUPPLY.minus(MOONINI_TEAM_AND_SUPPORTER_AMOUNT).minus(MOONINI_IDO_AMOUNT);

export const TOKEN_SYMBOL = {
  BNB: 'BNB',
  SOL: 'SOL',
  ETH: 'ETH',
  TON: 'TON',
  TRX: 'TRX',
  BTC: 'BTC',
  USDT: 'USDT',
} as const;

export const TOKEN_ID = {
  BNB: `${TOKEN_SYMBOL.BNB}:18:${NETWORKS.BSC}`,
  SOL: `${TOKEN_SYMBOL.SOL}:9:${NETWORKS.SOLANA}`,
  ETH: `${TOKEN_SYMBOL.ETH}:18:${NETWORKS.ETHEREUM}`,
  TON: `${TOKEN_SYMBOL.TON}:9:${NETWORKS.TON}`,
  TRX: `${TOKEN_SYMBOL.TRX}:6:${NETWORKS.TRON}`,
  BTC: `${TOKEN_SYMBOL.BTC}:8:${NETWORKS.BTC}`,
} as const;

export const WALLET_ID = {
  OFFICIAL_SOLANA_WALLET: 'official-solana-wallet-v2',
  OFFICIAL_ETHEREUM_WALLET: 'official-ethereum-wallet-v2',
  OFFICIAL_TON_WALLET: 'official-ton-wallet-v2',
  OFFICIAL_TRON_WALLET: 'official-tron-wallet-v2',
  OFFICIAL_BTC_WALLET: 'official-bitcoin-wallet-v2',
  OFFICIAL_BSC_WALLET: 'official-bsc-wallet-v2',
  MOONINI_STABILIZER_WALLET: 'moonini-stabilizer-wallet',
};

export const TELEGRAM_CHAT_IDS = {
  LOG: '-1003215604454',
  YVES: '396943893',
  OFFICIAL: process.env.NEXT_PUBLIC_APP_ENV === 'production'
    ? 'moonpacketchat'
    : 'moonpackettest',
};

export const MOONPACKET_RD_WALLETS = [
  {
    network: NETWORKS.SOLANA,
    address: '2zEhf2aCTZdTWKTjDkpGtd1rceTRVcxJoNTCv5DiKS83',
    description: 'Moonpacket RD Solana Wallet',
  },
  {
    network: NETWORKS.BSC,
    address: CONSOLE_WALLET_ADDRESSES.ROOT,
    description: 'Moonpacket RD BSC Wallet',
  },
  {
    network: NETWORKS.ETHEREUM,
    address: CONSOLE_WALLET_ADDRESSES.ROOT,
    description: 'Moonpacket RD Ethereum Wallet',
  },
  {
    network: NETWORKS.BSC,
    address: '0x66BF8157Ccdab7cc2a0f3f96DaD9e5517B46EFD1',
    description: 'Contract Deployer Wallet',
  },
  {
    network: NETWORKS.ETHEREUM,
    address: '0x0000000000000000000000000000000000000000',
    description: 'Ethereum System Wallet',
  },
  {
    network: NETWORKS.BSC,
    address: '0x14b1522e9Ec6AF27ce3F37804B68F1f1bE33c985',
    description: 'Emergency Withdrawal Wallet',
  },
  {
    network: NETWORKS.BSC,
    address: '0x284Cc0a084927f6B0147C2C5a909f032CaA89Bfa',
    description: 'Moonpacket Operator Wallet',
  }
];

export const MOONPACKET_EOA_WALLETS_MAP: Record<string, string> = process.env.NEXT_PUBLIC_APP_ENV === 'production' ? {
  'Cw2tnLijghtGwQTEq2V6KYDPjoyssHL2Ausi4kpHKtZG': 'Official Solana Wallet',
  '0x00451d379548cE9B828390f91A54D40111115397': 'Official Ethereum Wallet',
  '0x85BaC8C2abF5b1cE7Bf5C08c1e5Db3091545aC3f': 'Official BSC Wallet',
  'UQBwf812ROPME76Y_Oa1JNXQg0LvcyLztZm69dD93Onzc0u7': 'Official TON Wallet',
  '0x284Cc0a084927f6B0147C2C5a909f032CaA89Bfa': 'Moonpacket Operator Wallet',
} : {
  '5g1QJWjSKuP2Pd2hbRffiSKPt7qgNvHgSN3m7nzRNbBM': 'Official Solana Wallet',
  '0x723324C6EB42a8D417d43699D93Ad0Df6DE2479B': 'Official Ethereum Wallet',
  '0x61c963Fb837d7299A16Eb57b4a823027df0A5a19': 'Official BSC Wallet',
  'UQCQKBWGHus1L3PFSqan_aI3M_4eddTOqqfS9APf4r6rhqqO': 'Official TON Wallet',
};

export const NETWORK_ICONS = {
  [NETWORKS.SOLANA]: '/images/chains/solana.png',
  [NETWORKS.BSC]: '/images/chains/bsc.png',
  [NETWORKS.ETHEREUM]: '/images/chains/ethereum.png',
  [NETWORKS.TON]: '/images/chains/ton.png',
  [NETWORKS.TRON]: '/images/chains/tron.png',
  [NETWORKS.BTC]: '/images/chains/btc.png',
};

export const NETWORK_NAMES = {
  [NETWORKS.SOLANA]: 'Solana',
  [NETWORKS.BSC]: 'BNB Smart Chain',
  [NETWORKS.ETHEREUM]: 'Ethereum',
  [NETWORKS.TON]: 'The Open Network',
  [NETWORKS.TRON]: 'Tron',
  [NETWORKS.BTC]: 'Bitcoin',
};

export enum AMOUNT_TYPES {
  FIXED = 'fixed',
  RANDOM = 'random',
}

export enum GIFT_TYPES {
  NORMAL = 'normal',
  EXCLUSIVE = 'exclusive',
  TASK = 'task',
  SHARE = 'share',
  CHAT_GROWTH = 'chat_growth',
}

export enum CLAIM_TYPES {
  NONE = 'none',
  CODE = 'code',
  CHAT = 'chat',
  API = 'api',
  SYSTEM = 'system',
  BONUS = 'bonus',
}

// 徽章等级配置
export const BADGE_LEVELS = {
  1: {
    name: 'Earth Traveler',
    dailyWithdrawCount: 1, // 单日提领次数
    maxSingleWithdrawUsd: 10, // 单次提领上限 (USD)
    maxDailyWithdrawUsd: 10, // 每日总提领上限 (USD)
  },
  2: {
    name: 'Moon Walker',
    dailyWithdrawCount: 3,
    maxSingleWithdrawUsd: 50,
    maxDailyWithdrawUsd: 100,
  },
  3: {
    name: 'Stellar Navigator',
    dailyWithdrawCount: 5,
    maxSingleWithdrawUsd: 500,
    maxDailyWithdrawUsd: 2000,
  },
} as const;

export type BadgeLevel = keyof typeof BADGE_LEVELS;

export const DEFAULT_GIFT_METADATA = {
  [GIFT_TYPES.NORMAL]: {
    [CLAIM_TYPES.NONE]: {},
  },
  [GIFT_TYPES.EXCLUSIVE]: {
    [CLAIM_TYPES.NONE]: {},
  },
  [GIFT_TYPES.SHARE]: {
    [CLAIM_TYPES.SYSTEM]: {},
  },
  [GIFT_TYPES.CHAT_GROWTH]: {
    [CLAIM_TYPES.BONUS]: {},
  },
  [GIFT_TYPES.TASK]: {
    [CLAIM_TYPES.CODE]: { claim_code: '' },
    [CLAIM_TYPES.CHAT]: { chat_id: '' },
    [CLAIM_TYPES.API]: {
      api_url: '',
      api_key: '',
    },
  },
};

export const REDIS_KEYS = {
  GIFT_CLAIM_TOTAL: (gift_id: string = '*') => `gift:${gift_id}:claim:total`,
  GIFT_CTR: (gift_id: string = '*') => `gift:${gift_id}:ctr`,
  GIFT_EXPIRED: (gift_id: string = '*', expired_date: DateInput = '*') =>
    `gift:${gift_id}:expired:${format(expired_date, 'YYYYMMDDHH', 'system')}`,
  WITHDRAW: (user_id: string = '*') => `withdraw:${user_id}`,
  WITHDRAW_COUNT: (user_id: string = '*') => `withdraw:count:${user_id}`,
  SYSTEM_STATUS: 'system:status',
  /** 是否將廣告獎勵記為 locked_amount（true/false），可由 Console Dashboard 切換 */
  AD_REWARD_LOCK: 'ad:reward:lock',
  /** 廣告訊息快取（依語系），TTL 4 小時 */
  CHAT_AD_MESSAGE_ID: 'official_chat:ad:message:id',
  AD_MESSAGE: (locale: string) => `ad:message:${locale}`,
};

export const MAINNET_USDT_TOKEN_ADDRESS = {
  [NETWORKS.SOLANA]: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
  [NETWORKS.BSC]: '0x55d398326f99059fF775485246999027B3197955',
  [NETWORKS.ETHEREUM]: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  [NETWORKS.TON]: 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs',
};

export const TESTNET_USDT_TOKEN_ADDRESS = {
  [NETWORKS.SOLANA]: undefined,
  [NETWORKS.BSC]: '0xd6E6D4104bd4a7C05c8F2E762F9b4657154687e0',
  [NETWORKS.ETHEREUM]: undefined,
  [NETWORKS.TON]: undefined,
};

export const USDT_TOKEN_ADDRESS = {
  [NETWORKS.SOLANA]: process.env.NEXT_PUBLIC_APP_ENV === 'production'
    ? MAINNET_USDT_TOKEN_ADDRESS[NETWORKS.SOLANA]
    : TESTNET_USDT_TOKEN_ADDRESS[NETWORKS.SOLANA],
  [NETWORKS.BSC]: process.env.NEXT_PUBLIC_APP_ENV === 'production'
    ? MAINNET_USDT_TOKEN_ADDRESS[NETWORKS.BSC]
    : TESTNET_USDT_TOKEN_ADDRESS[NETWORKS.BSC],
  [NETWORKS.ETHEREUM]: process.env.NEXT_PUBLIC_APP_ENV === 'production'
    ? MAINNET_USDT_TOKEN_ADDRESS[NETWORKS.ETHEREUM]
    : TESTNET_USDT_TOKEN_ADDRESS[NETWORKS.ETHEREUM],
  [NETWORKS.TON]: process.env.NEXT_PUBLIC_APP_ENV === 'production'
    ? MAINNET_USDT_TOKEN_ADDRESS[NETWORKS.TON]
    : TESTNET_USDT_TOKEN_ADDRESS[NETWORKS.TON],
};

export const UNIFIED_TOKENS = [
  {
    id: 'USDT',
    name: 'USDT',
    symbol: 'USDT',
    icon: '/images/tokens/usdt.png',
    tokens: [
      {
        network: NETWORKS.SOLANA,
        token_address: USDT_TOKEN_ADDRESS[NETWORKS.SOLANA],
      },
      {
        network: NETWORKS.BSC,
        token_address: USDT_TOKEN_ADDRESS[NETWORKS.BSC],
      },
      {
        network: NETWORKS.ETHEREUM,
        token_address: USDT_TOKEN_ADDRESS[NETWORKS.ETHEREUM],
      },
      {
        network: NETWORKS.TON,
        token_address: USDT_TOKEN_ADDRESS[NETWORKS.TON],
      },
    ].filter(token => token.token_address !== undefined),
  },
];
